import React, { useState, useEffect, useCallback } from "react";
import { getCurrentUser, signOut, fetchAuthSession } from "aws-amplify/auth";
import { Settings, LogOut, Plus, Trash2, Edit2, RefreshCw, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CustomLogin from "../components/CustomLogin";
import "./AdminPage.css";

const AdminPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeds, setFeeds] = useState([]);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormData, setAddFormData] = useState({ url: '', site_name: '' });
  const [addFormLoading, setAddFormLoading] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [editFormData, setEditFormData] = useState({ url: '', site_name: '' });
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [deletingFeed, setDeletingFeed] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [refreshingFeeds, setRefreshingFeeds] = useState(false);

  // GraphQL configuration
  const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
  const apiKey = process.env.REACT_APP_GRAPHQL_API_KEY;
  const useRealGraphQL = process.env.REACT_APP_USE_REAL_GRAPHQL === 'true';

  // Debug logging
  console.log('🔧 Admin GraphQL Config:', {
    hasEndpoint: !!graphqlEndpoint,
    hasApiKey: !!apiKey,
    useRealGraphQL,
    endpoint: graphqlEndpoint ? `${graphqlEndpoint.substring(0, 50)}...` : 'Not set'
  });

  // Verify GraphQL configuration is working
  useEffect(() => {
    if (useRealGraphQL && graphqlEndpoint && apiKey) {
      console.log('✅ Admin GraphQL Configuration: READY for real API calls');
    } else {
      console.log('⚠️ Admin GraphQL Configuration: Will use MOCK data');
      console.log('  - useRealGraphQL:', useRealGraphQL);
      console.log('  - hasEndpoint:', !!graphqlEndpoint);
      console.log('  - hasApiKey:', !!apiKey);
    }
  }, [useRealGraphQL, graphqlEndpoint, apiKey]);

  // Fetch feeds from GraphQL API
  const loadFeeds = useCallback(async () => {
    if (!useRealGraphQL || !graphqlEndpoint || !apiKey) {
      console.log('🔧 Using mock feeds - GraphQL not configured');
      const mockFeeds = [
        {
          url: "https://feeds.feedburner.com/TechCrunch",
          site_name: "TechCrunch",
          article_count: 25,
          last_updated: new Date().toISOString(),
          status: "active",
        },
        {
          url: "https://rss.cnn.com/rss/edition.rss",
          site_name: "CNN",
          article_count: 18,
          last_updated: new Date(Date.now() - 3600000).toISOString(),
          status: "active",
        }
      ];
      setFeeds(mockFeeds);
      return;
    }

    const query = `
      query GetFeeds {
        getFeeds {
          url
          site_name
          last_updated
          article_count
          most_recent_article_date
          status
        }
      }
    `;

    setFeedsLoading(true);
    try {
      console.log('📡 Fetching RSS feeds from GraphQL API...');
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      if (data.data && data.data.getFeeds) {
        const feedsData = data.data.getFeeds;
        setFeeds(feedsData);
        console.log(`✅ Loaded ${feedsData.length} RSS feeds from database`);
        setStatusMessage(`Successfully loaded ${feedsData.length} RSS feeds`);
      } else {
        throw new Error('No feeds data received');
      }
    } catch (err) {
      console.error('❌ Error fetching feeds:', err);
      setStatusMessage(`Error loading feeds: ${err.message}`);
      // Fallback to empty array on error
      setFeeds([]);
    } finally {
      setFeedsLoading(false);
    }
  }, [graphqlEndpoint, apiKey, useRealGraphQL]);

  // Add new RSS feed
  const addFeed = async (url, siteName) => {
    if (!useRealGraphQL || !graphqlEndpoint || !apiKey) {
      setStatusMessage('Error: GraphQL not configured for adding feeds');
      return false;
    }

    const mutation = `
      mutation AddFeed($url: String!, $site_name: String!) {
        addFeed(url: $url, site_name: $site_name) {
          url
          site_name
          last_updated
          article_count
          most_recent_article_date
          status
        }
      }
    `;

    try {
      console.log('➕ Adding new RSS feed:', { url, siteName });
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ 
          query: mutation,
          variables: { url, site_name: siteName }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      if (data.data && data.data.addFeed) {
        const newFeed = data.data.addFeed;
        console.log('✅ Successfully added RSS feed:', newFeed);
        setStatusMessage(`Successfully added RSS feed: ${siteName}`);
        
        // Add to local state for immediate UI update
        setFeeds(prevFeeds => [...prevFeeds, newFeed]);
        
        return true;
      } else {
        throw new Error('No feed data received from addFeed mutation');
      }
    } catch (err) {
      console.error('❌ Error adding feed:', err);
      setStatusMessage(`Error adding feed: ${err.message}`);
      return false;
    }
  };

  // Handle add feed form submission
  const handleAddFeed = async (e) => {
    e.preventDefault();
    
    const { url, site_name } = addFormData;
    
    // Validation
    if (!url.trim() || !site_name.trim()) {
      setStatusMessage('Error: Both URL and Site Name are required');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setStatusMessage('Error: Please enter a valid URL');
      return;
    }

    // Check for duplicate URLs
    if (feeds.some(feed => feed.url === url.trim())) {
      setStatusMessage('Error: This RSS feed URL already exists');
      return;
    }

    setAddFormLoading(true);
    const success = await addFeed(url.trim(), site_name.trim());
    
    if (success) {
      // Reset form
      setAddFormData({ url: '', site_name: '' });
      setShowAddForm(false);
    }
    
    setAddFormLoading(false);
  };

  // Update existing RSS feed
  const updateFeed = async (oldUrl, newUrl, newSiteName) => {
    if (!useRealGraphQL || !graphqlEndpoint || !apiKey) {
      setStatusMessage('Error: GraphQL not configured for updating feeds');
      return false;
    }

    const mutation = `
      mutation UpdateFeed($old_url: String!, $new_url: String!, $new_site_name: String!) {
        updateFeed(old_url: $old_url, new_url: $new_url, new_site_name: $new_site_name) {
          url
          site_name
          last_updated
          article_count
          most_recent_article_date
          status
        }
      }
    `;

    try {
      console.log('✏️ Updating RSS feed:', { oldUrl, newUrl, newSiteName });
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ 
          query: mutation,
          variables: { old_url: oldUrl, new_url: newUrl, new_site_name: newSiteName }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      if (data.data && data.data.updateFeed) {
        const updatedFeed = data.data.updateFeed;
        console.log('✅ Successfully updated RSS feed:', updatedFeed);
        setStatusMessage(`Successfully updated RSS feed: ${newSiteName}`);
        
        // Update local state for immediate UI update
        setFeeds(prevFeeds => 
          prevFeeds.map(feed => 
            feed.url === oldUrl ? updatedFeed : feed
          )
        );
        
        return true;
      } else {
        throw new Error('No feed data received from updateFeed mutation');
      }
    } catch (err) {
      console.error('❌ Error updating feed:', err);
      setStatusMessage(`Error updating feed: ${err.message}`);
      return false;
    }
  };

  // Handle edit feed form submission
  const handleEditFeed = async (e) => {
    e.preventDefault();
    
    if (!editingFeed) return;
    
    const { url, site_name } = editFormData;
    
    // Validation
    if (!url.trim() || !site_name.trim()) {
      setStatusMessage('Error: Both URL and Site Name are required');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setStatusMessage('Error: Please enter a valid URL');
      return;
    }

    // Check for duplicate URLs (excluding current feed)
    if (url.trim() !== editingFeed.url && feeds.some(feed => feed.url === url.trim())) {
      setStatusMessage('Error: This RSS feed URL already exists');
      return;
    }

    setEditFormLoading(true);
    const success = await updateFeed(editingFeed.url, url.trim(), site_name.trim());
    
    if (success) {
      // Close edit modal
      setEditingFeed(null);
      setEditFormData({ url: '', site_name: '' });
    }
    
    setEditFormLoading(false);
  };

  // Open edit modal
  const openEditModal = (feed) => {
    setEditingFeed(feed);
    setEditFormData({ url: feed.url, site_name: feed.site_name });
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditingFeed(null);
    setEditFormData({ url: '', site_name: '' });
  };

  // Delete RSS feed
  const deleteFeed = async (url) => {
    if (!useRealGraphQL || !graphqlEndpoint || !apiKey) {
      setStatusMessage('Error: GraphQL not configured for deleting feeds');
      return false;
    }

    const mutation = `
      mutation RemoveFeed($url: String!) {
        removeFeed(url: $url)
      }
    `;

    try {
      console.log('🗑️ Deleting RSS feed:', url);
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ 
          query: mutation,
          variables: { url }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      if (data.data && data.data.removeFeed) {
        console.log('✅ Successfully deleted RSS feed:', url);
        console.log('📊 GraphQL response data:', data.data);
        setStatusMessage(`Successfully deleted RSS feed`);
        
        // Remove from local state for immediate UI update
        setFeeds(prevFeeds => prevFeeds.filter(feed => feed.url !== url));
        
        console.log('🎯 Returning true from deleteFeed');
        return true;
      } else {
        console.log('❌ GraphQL response data:', data.data);
        console.log('❌ removeFeed value:', data.data?.removeFeed);
        throw new Error('Failed to delete feed - removeFeed returned false');
      }
    } catch (err) {
      console.error('❌ Error deleting feed:', err);
      console.error('❌ Error details:', err.message);
      setStatusMessage(`Error deleting feed: ${err.message}`);
      console.log('🎯 Returning false from deleteFeed due to error');
      return false;
    }
  };

  // Handle delete confirmation
  const handleDeleteFeed = async () => {
    if (!deletingFeed) return;
    
    console.log('🚀 Starting delete process for:', deletingFeed.url);
    setDeleteLoading(true);
    
    try {
      const success = await deleteFeed(deletingFeed.url);
      console.log('🔍 Delete result:', success);
      
      // Always reset loading state first
      setDeleteLoading(false);
      
      if (success) {
        console.log('✅ Delete successful, closing modal');
        // Close delete confirmation after resetting loading state
        setDeletingFeed(null);
      } else {
        console.log('❌ Delete failed, keeping modal open');
        // Keep modal open on failure so user can see the error message
      }
    } catch (error) {
      console.error('💥 Error in handleDeleteFeed:', error);
      setDeleteLoading(false);
      // Keep modal open on error
    }
  };

  // Open delete confirmation
  const openDeleteConfirmation = (feed) => {
    setDeletingFeed(feed);
  };

  // Close delete confirmation
  const closeDeleteConfirmation = () => {
    setDeletingFeed(null);
  };

  // Refresh all RSS feeds
  const refreshAllFeeds = async () => {
    if (!useRealGraphQL || !graphqlEndpoint || !apiKey) {
      setStatusMessage('Error: GraphQL not configured for refreshing feeds');
      return false;
    }

    const mutation = `
      mutation RefreshFeeds {
        refreshFeeds
      }
    `;

    setRefreshingFeeds(true);
    try {
      console.log('🔄 Triggering RSS feeds refresh...');
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ query: mutation }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      if (data.data && data.data.refreshFeeds) {
        console.log('✅ Successfully triggered RSS feeds refresh');
        setStatusMessage('RSS feeds refresh triggered successfully. New articles will be processed shortly.');
        
        // Reload feeds to get updated status
        setTimeout(() => {
          loadFeeds();
        }, 2000);
        
        return true;
      } else {
        throw new Error('Failed to trigger feeds refresh');
      }
    } catch (err) {
      console.error('❌ Error refreshing feeds:', err);
      setStatusMessage(`Error refreshing feeds: ${err.message}`);
      return false;
    } finally {
      setRefreshingFeeds(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  // Get feed status color
  const getFeedStatusColor = (feed) => {
    if (!feed.last_updated) return 'unknown';
    
    const lastUpdate = new Date(feed.last_updated);
    const now = new Date();
    const hoursSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60);
    
    if (hoursSinceUpdate < 2) return 'active';
    if (hoursSinceUpdate < 24) return 'recent';
    if (hoursSinceUpdate < 168) return 'stale'; // 1 week
    return 'inactive';
  };

  // Check authentication status
  const checkAuthState = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log("✅ User authenticated:", currentUser.username);
      
      // For federated users, get additional information from the auth session
      const session = await fetchAuthSession();
      
      // Extract user information from ID token claims for federated users
      let userInfo = {
        username: currentUser.username,
        attributes: currentUser.attributes || {}
      };
      
      // If this is a federated user (EntraID), extract name from ID token
      if (session.tokens?.idToken) {
        const idTokenPayload = session.tokens.idToken.payload;
        
        // Extract user information from EntraID claims
        userInfo = {
          ...userInfo,
          displayName: idTokenPayload.name || idTokenPayload.given_name || idTokenPayload.preferred_username,
          email: idTokenPayload.email || userInfo.attributes.email,
          givenName: idTokenPayload.given_name,
          familyName: idTokenPayload.family_name,
          preferredUsername: idTokenPayload.preferred_username,
          upn: idTokenPayload.upn,
          uniqueName: idTokenPayload.unique_name
        };
      }
      
      setUser(userInfo);
      loadFeeds();
    } catch (error) {
      console.log("❌ User not authenticated:", error.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadFeeds]);

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  const handleSignOut = async () => {
    try {
      console.log("🚪 Signing out user...");
      
      // Immediately redirect to home page to avoid showing login screen
      navigate("/");
      
      // Then perform the sign out in the background
      await signOut();
      
      // Clear local state
      setUser(null);
      setFeeds([]);
      
      console.log("✅ Sign out completed");
    } catch (error) {
      console.error("❌ Sign-out error:", error);
      // Even if sign out fails, we've already redirected to home
      // This prevents the user from seeing any error state
    }
  };

  // Clear status message after 5 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <CustomLogin onSuccess={checkAuthState} />;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-title">
          <Settings size={24} />
          <h1>RSS Aggregator Admin</h1>
        </div>
        <div className="admin-user">
          <span>Welcome, {user.displayName || user.email || user.preferredUsername || user.upn || user.uniqueName || user.username}</span>
          <button onClick={() => navigate("/")} className="home-button">
            <Home size={16} />
            Home
          </button>
          <button onClick={handleSignOut} className="logout-button">
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </div>

      <div className="admin-content">
        {statusMessage && (
          <div className={`status-message ${statusMessage.includes('Error') ? 'error' : 'success'}`}>
            {statusMessage}
          </div>
        )}

        <div className="admin-section">
          <div className="section-header">
            <h2>RSS Feeds ({feeds.length})</h2>
            <div className="header-actions">
              <button 
                onClick={() => setShowAddForm(!showAddForm)} 
                className="add-button"
              >
                <Plus size={16} />
                Add Feed
              </button>
              <button 
                onClick={refreshAllFeeds} 
                className="refresh-all-button"
                disabled={refreshingFeeds}
                title="Trigger RSS feeds refresh"
              >
                <RefreshCw size={16} className={refreshingFeeds ? 'spinning' : ''} />
                {refreshingFeeds ? 'Refreshing...' : 'Refresh All'}
              </button>
              <button 
                onClick={loadFeeds} 
                className="refresh-button"
                disabled={feedsLoading}
                title="Reload feeds list"
              >
                <RefreshCw size={16} className={feedsLoading ? 'spinning' : ''} />
                {feedsLoading ? 'Loading...' : 'Reload'}
              </button>
            </div>
          </div>

          {/* Add Feed Form */}
          {showAddForm && (
            <div className="add-feed-form">
              <h3>Add New RSS Feed</h3>
              <form onSubmit={handleAddFeed}>
                <div className="form-group">
                  <label htmlFor="feed-url">RSS Feed URL *</label>
                  <input
                    id="feed-url"
                    type="url"
                    value={addFormData.url}
                    onChange={(e) => setAddFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://example.com/rss"
                    required
                    disabled={addFormLoading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="site-name">Site Name *</label>
                  <input
                    id="site-name"
                    type="text"
                    value={addFormData.site_name}
                    onChange={(e) => setAddFormData(prev => ({ ...prev, site_name: e.target.value }))}
                    placeholder="Example News Site"
                    required
                    disabled={addFormLoading}
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={addFormLoading}
                  >
                    {addFormLoading ? (
                      <>
                        <RefreshCw size={16} className="spinning" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Add Feed
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowAddForm(false);
                      setAddFormData({ url: '', site_name: '' });
                    }}
                    className="cancel-button"
                    disabled={addFormLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {feedsLoading ? (
            <div className="feeds-loading">
              <div className="loading-spinner"></div>
              <p>Loading RSS feeds...</p>
            </div>
          ) : feeds.length === 0 ? (
            <div className="no-feeds">
              <p>No RSS feeds found. Add your first feed to get started!</p>
            </div>
          ) : (
            <div className="feeds-list">
              {feeds.map((feed) => (
                <div key={feed.url} className="feed-item">
                  <div className="feed-info">
                    <div className="feed-main">
                      <h3>{feed.site_name}</h3>
                      <div className="feed-url-container">
                        <a href={feed.url} target="_blank" rel="noopener noreferrer" className="feed-url">
                          {feed.url}
                        </a>
                      </div>
                    </div>
                    <div className="feed-stats">
                      {feed.article_count !== undefined && (
                        <span className="article-count">
                          📰 {feed.article_count} articles
                        </span>
                      )}
                      {feed.last_updated && (
                        <span className="last-updated">
                          🕒 Updated: {formatDate(feed.last_updated)}
                        </span>
                      )}
                      {feed.most_recent_article_date && (
                        <span className="recent-article">
                          📅 Latest: {formatDate(feed.most_recent_article_date)}
                        </span>
                      )}
                      <span className={`feed-status ${getFeedStatusColor(feed)}`}>
                        {getFeedStatusColor(feed)}
                      </span>
                    </div>
                  </div>
                  <div className="feed-actions">
                    <button 
                      className="edit-button" 
                      title="Edit Feed"
                      onClick={() => openEditModal(feed)}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      className="delete-button" 
                      title="Delete Feed"
                      onClick={() => openDeleteConfirmation(feed)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Feed Modal */}
      {editingFeed && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit RSS Feed</h3>
              <button className="modal-close" onClick={closeEditModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleEditFeed}>
              <div className="form-group">
                <label htmlFor="edit-feed-url">RSS Feed URL *</label>
                <input
                  id="edit-feed-url"
                  type="url"
                  value={editFormData.url}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com/rss"
                  required
                  disabled={editFormLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-site-name">Site Name *</label>
                <input
                  id="edit-site-name"
                  type="text"
                  value={editFormData.site_name}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, site_name: e.target.value }))}
                  placeholder="Example News Site"
                  required
                  disabled={editFormLoading}
                />
              </div>
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={editFormLoading}
                >
                  {editFormLoading ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Edit2 size={16} />
                      Update Feed
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  onClick={closeEditModal}
                  className="cancel-button"
                  disabled={editFormLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingFeed && (
        <div className="modal-overlay" onClick={closeDeleteConfirmation}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete RSS Feed</h3>
              <button className="modal-close" onClick={closeDeleteConfirmation}>
                ×
              </button>
            </div>
            <div className="delete-content">
              <div className="warning-icon">
                <Trash2 size={48} />
              </div>
              <p>Are you sure you want to delete this RSS feed?</p>
              <div className="feed-preview">
                <strong>{deletingFeed.site_name}</strong>
                <br />
                <span className="feed-url-preview">{deletingFeed.url}</span>
              </div>
              <p className="warning-text">
                This action cannot be undone. All articles from this feed will remain, 
                but no new articles will be fetched.
              </p>
              <div className="delete-actions">
                <button 
                  onClick={handleDeleteFeed}
                  className="delete-confirm-button"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Yes, Delete Feed
                    </>
                  )}
                </button>
                <button 
                  onClick={closeDeleteConfirmation}
                  className="cancel-button"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
